import { getBackendUrl } from "@Utils";
import axiosWrapper from "../../services/axiosWrapper";
import { statusCategories } from "../PortalSetings/Configuration/PortalLevelStatus/StatusConstants";
import i18n from "@i18n";
import { convertDate } from "@Services/luxonHelper";

const { t }: any = i18n;

export const fileStatusHelper = (args: any): any => {
  const { resourceType } = args;
  const resource: any = { resourceType };

  if (resourceType === "RESOURCE_TYPE_FILE" || resourceType === "RESOURCE_TYPE_EMBED") {
    const { fileVersionId } = args;
    resource.fileVersionId = fileVersionId;
  }

  if (resourceType === "RESOURCE_TYPE_ESTIMATE") {
    const { estimateId, estimateVersionId } = args;
    resource.estimateVersionId = estimateVersionId;
    resource.estimateId = estimateId;
  }

  if (resourceType === "RESOURCE_TYPE_TASK") {
    const { taskId } = args;
    resource.taskId = taskId;
  }

  if (resourceType === "RESOURCE_TYPE_ISSUE") {
    const { issueId } = args;
    resource.issueId = issueId;
  }

  return resource;
};

export const getResourceDetails = (resources: any[]): any[] =>
  resources.map((resource: any) => {
    const { resourceType } = resource;

    if (resourceType === "RESOURCE_TYPE_FILE" || resourceType === "RESOURCE_TYPE_EMBED") {
      const { fileVersion: fileVersionId, name } = resource;
      return { fileVersionId, resourceType, name };
    }

    if (resourceType === "RESOURCE_TYPE_MERGED_FILE") {
      const { fileVersion: fileVersionId } = resource;
      return { fileVersionId, resourceType };
    }

    if (resourceType === "RESOURCE_TYPE_ESTIMATE") {
      const { _id: estimateId, versionId: estimateVersionId } = resource;
      return { estimateId, estimateVersionId, resourceType };
    }

    if (resourceType === "RESOURCE_TYPE_ISSUE") {
      const { issueId } = resource;
      return { issueId, resourceType };
    }

    if (resourceType === "RESOURCE_TYPE_TASKS") {
      const { taskId } = resource;
      return { taskId, resourceType: "RESOURCE_TYPE_TASK" };
    }
  });

export const getApprovalStagesToShow = (
  approvalStages: any[],
  isRollback: boolean,
  skipReviewTillLevel: number,
  rollbackReviewToLevel: number,
): any => {
  let filteredStages = filterApprovalStages(approvalStages, isRollback, skipReviewTillLevel, rollbackReviewToLevel);

  const isReviewInitiated = filteredStages[0]?.clearanceStatus !== "pending";
  const willReviewClose = !isRollback && filteredStages.at(-1)?.clearanceStatus === "in_progress";

  const { startIndex, rollbackIndex } = getStartIndexAndRollbackIndex(
    filteredStages,
    isRollback,
    rollbackReviewToLevel,
  );

  const numberOfStagesToShow = isReviewInitiated ? 4 : 3;
  const endIndex = startIndex + numberOfStagesToShow;

  const showStartLine = startIndex >= 0;
  const showEndLine = endIndex < filteredStages.length;

  const approvalStagesToShow = filteredStages.slice(startIndex, endIndex).map((stage: any, idx: number) => ({
    ...stage,
    rollbackTo: rollbackIndex !== null && startIndex + idx === rollbackIndex,
  }));

  return {
    showStartLine,
    showEndLine,
    approvalStagesToShow,
    isReviewInitiated,
    willReviewClose,
  };
};

const filterApprovalStages = (
  approvalStages: any[],
  isRollback: boolean,
  skipReviewTillLevel: number,
  rollbackReviewToLevel: number,
): any[] => {
  let filteredStages = [...approvalStages];

  if (skipReviewTillLevel) {
    const approvalStageInProgressIndex = findApprovalStageInProgressIndex(filteredStages);
    filteredStages = filteredStages.filter(
      (stage, index) => index <= approvalStageInProgressIndex || index >= skipReviewTillLevel,
    );
  } else if (rollbackReviewToLevel && isRollback) {
    const approvalStageInProgressIndex = findApprovalStageInProgressIndex(filteredStages);
    const approvalStageApprovedIndex = filteredStages.findLastIndex(
      (stage: any) => stage.serial === rollbackReviewToLevel,
    );
    filteredStages = filteredStages.filter(
      (stage, index) => index <= approvalStageApprovedIndex || index >= approvalStageInProgressIndex,
    );
  } else if (isRollback) {
    const approvalStageInProgressIndex = findApprovalStageInProgressIndex(filteredStages);
    const approvalStageApprovedIndex = filteredStages.findLastIndex(
      (stage: any) => stage.clearanceStatus === "approved",
    );
    filteredStages = filteredStages.filter(
      (stage, index) => index < approvalStageApprovedIndex + 1 || index >= approvalStageInProgressIndex,
    );
  }

  return filteredStages;
};

const findApprovalStageInProgressIndex = (approvalStages: any[]): number => {
  return approvalStages.findIndex((stage: any) => stage.clearanceStatus === "in_progress");
};

const getStartIndexAndRollbackIndex = (
  approvalStages: any[],
  isRollback: boolean,
  rollbackReviewToLevel: number,
): { startIndex: number; rollbackIndex: any } => {
  let startIndex = 0;
  let rollbackIndex: any = null;

  const approvalStageInProgressIndex = findApprovalStageInProgressIndex(approvalStages);
  const totalStages = approvalStages.length;

  if (approvalStageInProgressIndex === -1 && approvalStages[0]?.clearanceStatus !== "pending") {
    startIndex = Math.max(totalStages - 4, 0);
  }

  if (approvalStageInProgressIndex === -1 || approvalStageInProgressIndex === 0) {
    startIndex = 0;
  }

  if (approvalStageInProgressIndex > 0) {
    if (isRollback && rollbackReviewToLevel) {
      rollbackIndex = rollbackReviewToLevel - 1;
    } else if (isRollback) {
      rollbackIndex = approvalStageInProgressIndex - 1;
    }

    const diff = totalStages - approvalStageInProgressIndex;

    if (diff === 1) {
      startIndex = Math.max(0, approvalStageInProgressIndex - 3);
    } else if (diff === 2) {
      startIndex = Math.max(0, approvalStageInProgressIndex - 2);
    } else if (diff > 2) {
      startIndex = Math.max(0, approvalStageInProgressIndex - 1);
    }
  }

  return { startIndex, rollbackIndex };
};

export const createNewReview = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/createReview`, data);

export const getReviewData = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/getReviewData`, data);

export const saveUserResponse = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/saveUserResponseForReview`, data);

export const userPermissionsToMarkResponse = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/userPermissionsToMarkReview`, data);

export const sendNotificationForReview = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/sendNotificationForReview`, data);

export const fetchListOfStatusesAvailable = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/fetchListOfStatusesAvailable`, data);

export const fetchBulkTransitionStatusList = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/fetchBulkTransitionStatusList`, data);

export const changeResourceStatusInReview = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/changeResourceStatusInReview`, data);

export const notifyUsersInLevel = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/notifyUsersInLevel`, data);

export const notifyUserInReview = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/notifyUserInReview`, data);

export const getFileStatusInReviews = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/getFileStatusInAllReviews`, data);

export const getReviewStatusForAllFileVersions = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/getReviewStatusForAllFileVersions`, data);

export const initiateReview = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/initiateReview`, data);

export const addResourcesToReview = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/addResourcesToReview`, data);

export const removeResourcesFromReview = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/removeResourcesFromReview`, data);

export const getIssueCustomFields = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/issues/getCustomFields`, data);

export const createStandaloneIssue = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/issues/getCustomFields`, data);

export const rollbackReview = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/rollbackReview`, data);

export const getReviewsListForEntity = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/getReviewsListForEntity`, data);

export const canUserClearReviewLevel = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/canUserClearReviewLevel`, data);

export const updateFilesInReview = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/updateFilesInReview`, data);

export const getResourcesInReview = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/getResourcesInReviewService`, data);

export const getIssuesDetailsInReview = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/getIssuesDetailsInReviewService`, data);

export const addSignatureToResources = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/addSignatureToResources`, data);

export const referencedFilesInReviews = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/referencedFilesInReviews`, data);

export const updateReviewStatus = async (data: any) => {
  return axiosWrapper.post(`${getBackendUrl()}/reviews/updateReviewStatus`, data);
};

export const checklistsInReviews = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/checklistsInReviews`, data);

export const manageChecklist = async (data: any) =>
  await axiosWrapper.post(`${getBackendUrl()}/reviews/manageChecklist`, data);

/**
 * Calculate the number of filled checklists for a resource
 * A checklist is considered "filled" if all questions have at least one selection
 * that matches the available choices count
 */
export const getFilledChecklistCount = (resource: any, workflow: any): number => {
  if (!resource || !workflow?.checklists) return 0;

  const checklistAssignments = resource.checklistAssignments || [];
  const checklistSelections = resource.checklistSelections || {};
  let filledCount = 0;

  checklistAssignments.forEach((assignment: any) => {
    const checklist = workflow.checklists.find((c: any) => c._id === assignment.checklistId);
    if (!checklist) return;

    const choicesCount = checklist.choices?.length || 0;
    if (choicesCount === 0) return;

    assignment.levelIds?.forEach((levelId: string) => {
      const sections = checklist.template || [];
      if (sections.length === 0) return;

      // Check if all questions in all sections are filled
      const isChecklistFilled = sections.every((section: any, sectionIndex: number) => {
        const questions = section.questionFields || [];
        return questions.every((question: any, questionIndex: number) => {
          const selected =
            checklistSelections[levelId]?.[checklist._id]?.[sectionIndex]?.[questionIndex]?.selected || [];
          return selected.length > 0;
        });
      });

      if (isChecklistFilled) {
        filledCount++;
      }
    });
  });

  return filledCount;
};

export const getReviewsGridColumns = ({
  creatorTemplate,
  createdDateTemplate,
  finishedDateTemplate,
  nextActionDueDateTemplate,
  waitingForTemplate,
  reviewSerialIdTemplate,
  reviewStageTemplate,
  workFlowTemplate,
  projectNameTemplate,
  navigateTemplate,
  showNavigateColumn = false,
}: any) => [
  ...(showNavigateColumn
    ? [
        {
          field: "id",
          headerText: "",
          width: "50",
          formatter: (data: any, params: any, a: any, b: any, value: any) => navigateTemplate(value),
          _valueGetter: (params: any) => params._id,
        },
      ]
    : []),
  {
    field: "serialId",
    id: "serialId",
    name: t("Id"),
    formatter: (data: any, params: any, a: any, b: any, value: any) => reviewSerialIdTemplate(value),
    _valueGetter: (params: any) => params.serialId,
  },
  {
    field: "name",
    id: "name",
    name: t("Name"),
    minWidth: 130,
    filterType: "Text",
  },
  {
    field: "description",
    id: "description",
    name: t("Description"),
    minWidth: 130,
    filterType: "Text",
  },
  {
    field: "workflow",
    id: "workflow",
    name: t("Review Flow"),
    minWidth: 120,
    formatter: (data: any, params: any, a: any, b: any, value: any) => workFlowTemplate(value),
    _valueGetter: (params: any) => params.workflow.name,
  },
  {
    field: "spaceName",
    id: "spaceName",
    minWidth: 130,
    name: t("Space"),
  },
  {
    field: "nextActionDueDate",
    id: "nextActionDueDate",
    name: t("Next Action Due"),
    minWidth: 150,
    formatter: (data: any, params: any, a: any, b: any, value: any) => nextActionDueDateTemplate(value),
    filterType: "Date",
    _valueGetter: (params: any) => convertDate(Number(params.nextActionDueDate), "dateForTooltip"),
  },
  {
    field: "currentReviewLevel",
    id: "currentReviewLevel",
    name: t("Waiting For"),
    minWidth: 90,
    formatter: (data: any, params: any, a: any, b: any, value: any) => waitingForTemplate(value),
    _valueGetter: (params: any) =>
      params.currentReviewLevel?.serial
        ? `${params.currentReviewLevel.serial} - ${params.currentReviewLevel.title}`
        : "-",
  },
  {
    field: "template",
    id: "template",
    name: t("Project Name"),
    minWidth: 130,
    formatter: (data: any, params: any, a: any, b: any, value: any) => projectNameTemplate(value),
  },
  {
    field: "finishedDate",
    id: "finishedDate",
    name: t("Finished On"),
    formatter: (data: any, params: any, a: any, b: any, value: any) => finishedDateTemplate(value),
    filterType: "Date",
    visible: false,
    minWidth: 130,
    _valueGetter: (params: any) => params.finishedDate,
  },
  {
    field: "numberOfEntitiesAttached",
    id: "numberOfEntitiesAttached",
    name: t("Contents"),
    minWidth: 60,
    visible: false,
    _valueGetter: (params: any) => params.numberOfEntitiesAttached,
  },
  {
    field: "reviewStatus",
    id: "reviewStatus",
    name: t("Stage"),
    isReviewColumnType: true,
    minWidth: 130,
    formatter: (data: any, params: any, a: any, b: any, value: any) => reviewStageTemplate(value),
    filterType: "Text",
    visible: false,
    _valueGetter: (params: any) => params.reviewStatus,
  },
  {
    field: "createdBy",
    id: "createdBy",
    name: t("Created by"),
    minWidth: 180,
    formatter: (data: any, params: any, a: any, b: any, value: any) => creatorTemplate(value),
    visible: false,
    _valueGetter: (params: any) => `${params.createdBy.firstName} ${params.createdBy.lastName}`,
  },
  {
    field: "createdDate",
    id: "createdDate",
    name: t("Created On"),
    minWidth: 150,
    formatter: (data: any, params: any, a: any, b: any, value: any) => createdDateTemplate(value),
    filterType: "Date",
    visible: false,
    _valueGetter: (params: any) => convertDate(Number(params.createdDate), "dateForTooltip"),
  },
];

export const getSelectUsersGridColumns = (): any[] => [
  {
    field: "fullName",
    id: "fullName",
    width: 125,
    // formatter: (data, params, a, b, value) => usersTemplate(value),
    name: t("User"),
    filterType: "Text",
  },
  {
    field: "companyName",
    id: "companyName",
    width: 125,
    name: t("Organization"),
    filterType: "Text",
  },
];

export const getSelectProjectsGridColumns = ({ projectImgDescTemplate }: any): any[] => [
  {
    field: "projectId",
    id: "ProjectId",
    minWidth: 150,
    formatter: (data: any, params: any, a: any, b: any, value: any) => projectImgDescTemplate(value),
    name: t("ID"),
    filterType: "Text",
    wrapText: true,
  },
  {
    field: "description",
    id: "description",
    minWidth: 150,
    name: t("Name"),
    filterType: "Text",
    wrapText: true,
  },
];

export const getSelectReviewsFlowsGridColumns = ({
  createdDateReviewFlowTemplate,
  createdByReviewFlowTemplate,
}: any) => [
  {
    field: "name",
    id: "name",
    name: t("Name"),
    filterType: "Text",
    wrapText: true,
  },
  {
    field: "description",
    id: "description",
    minWidth: 150,
    name: t("Description"),
    filterType: "Text",
    wrapText: true,
  },
  {
    field: "createdBy",
    id: "createdBy",
    name: t("Created By"),
    formatter: (data: any, params: any, a: any, b: any, value: any) => createdByReviewFlowTemplate(value),
  },
  {
    field: "createdDate",
    id: "createdDate",
    name: t("Created On"),
    formatter: (data: any, params: any, a: any, b: any, value: any) => createdDateReviewFlowTemplate(value),
    filterType: "Date",
  },
];

export const getFilesListToolbar = (): any[] => [
  {
    id: "file_properties",
    text: "Properties",
    align: "Right",
    disabled: true,
  },
  "ColumnChooser",
];

export const getFilesInReviewToolbar = ({
  addBtnTemplate,
  updateBtnTemplate,
  removeBtnTemplate,
  fileActivityBtnTemplate,
  bulkTransitionBtnTemplate,
  addSignatureToFileBtnTemplate,
  propertiesBtnTemplate,
  excelExportReviews,
  downloadReviewsFiles,
  showProperties,
  allowSendTransmittals,
}: any): any[] => {
  const list: any[] = [
    { id: "add_file", align: "Left", template: addBtnTemplate },
    { id: "update_file", align: "Left", template: updateBtnTemplate },
    { id: "remove_file", align: "Left", template: removeBtnTemplate },
    { id: "file_activity", align: "Left", template: fileActivityBtnTemplate },
    {
      id: "add_signature",
      align: "Left",
      template: addSignatureToFileBtnTemplate,
    },
    {
      id: "download_reviews_files",
      align: "Left",
      template: downloadReviewsFiles,
    },
    {
      id: "bulk_transition",
      align: "Left",
      template: bulkTransitionBtnTemplate,
    },
  ];
  if (allowSendTransmittals) {
    list.push({
      id: "excel_export_reviews",
      align: "Left",
      template: excelExportReviews,
    });
  }

  if (showProperties) {
    list.push(
      {
        id: "file_properties",
        align: "Right",
        template: propertiesBtnTemplate,
      },
      "ColumnChooser",
    );
  }

  return list;
};

export const getIssuesInReviewToolbar = ({
  addBtnTemplate,
  removeBtnTemplate,
  exportBtnTemplate,
  exportPDFBtnTemplate,
  propertiesBtnTemplate,
  showProperties,
}: any): any[] => {
  const list: any[] = [
    { id: "add_issue", align: "Left", template: addBtnTemplate },
    { id: "remove_issue", align: "Left", template: removeBtnTemplate },
    { id: "excel_export", align: "Left", template: exportBtnTemplate },
    { id: "pdf_export", align: "Left", template: exportPDFBtnTemplate },
  ];

  if (showProperties) {
    list.push(
      {
        id: "issue_properties",
        align: "Right",
        template: propertiesBtnTemplate,
      },
      "ColumnChooser",
    );
  }

  return list;
};

export const getFilesInReviewGridColumns = ({
  openFileInViewerTemplate,
  fileStatusTemplate,
  fileSizeTemplate,
  areIssuesPresentTemplate,
  fileVersionTemplate,
  fileTypeTemplate,
}: any): any[] => [
  {
    type: "checkbox",
    width: 40,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileVersionId",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    width: "30",
    headerText: "",
    textAlign: "Center",
    template: openFileInViewerTemplate,
    editTemplate: openFileInViewerTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    width: "30",
    headerText: "",
    textAlign: "Center",
    template: areIssuesPresentTemplate,
    editTemplate: areIssuesPresentTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "resourceType",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "details.name",
    headerText: "Name",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileType",
    headerText: "Type",
    template: fileTypeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    width: "60",
  },
  {
    field: "versionNumber",
    headerText: "Version",
    template: fileVersionTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    width: "60",
  },
  {
    field: "size",
    headerText: "Size",
    template: fileSizeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    width: "60",
  },
  {
    field: "status",
    headerText: "Status",
    template: fileStatusTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getIssuesInReviewGridColumns = ({
  openIssueInViewerTemplate,
  officialResponseTemplate,
  severityTemplate,
  issueImageTemplate,
  fileVersionTemplate,
  issueStatusTemplate,
  issueTitleTemplate,
  fileNameTemplate,
}: any): any[] => [
  {
    type: "checkbox",
    width: 50,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "issueId",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "resourceType",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    width: "30",
    headerText: "",
    textAlign: "Center",
    template: openIssueInViewerTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "snapshot",
    width: "100",
    headerText: "Image",
    textAlign: "Center",
    template: issueImageTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "title",
    headerText: "Title",
    template: issueTitleTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "officialResponse",
    headerText: "Official Response",
    template: officialResponseTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "severity",
    headerText: "Severity",
    template: severityTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "file",
    headerText: "File",
    template: fileNameTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileVersionNumber",
    headerText: "File Version",
    template: fileVersionTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "status",
    headerText: "Status",
    template: issueStatusTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getResourcesActivityColumns = ({
  openFileInViewerTemplate,
  fileNameTemplate,
  fileSizeTemplate,
  versionTemplate,
  creatorTemplate,
  dateTemplate,
  statusTemplate,
  modificationTypeTemplate,
}: any): any[] => [
  {
    field: "fileVersionId",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    isPrimaryKey: false,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    headerText: "",
    width: "30",
    textAlign: "Center",
    template: openFileInViewerTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileName",
    headerText: "Name",
    template: fileNameTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileSize",
    headerText: "File Size",
    template: fileSizeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "versionNumber",
    headerText: "Version",
    template: versionTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "updatedBy",
    headerText: "Modified by",
    template: creatorTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "updatedDate",
    headerText: "Modified Date",
    template: dateTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "status",
    headerText: "Last Status",
    template: statusTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "type",
    headerText: "Modification Type",
    template: modificationTypeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getStatusBtnStyle = (category: any): any => {
  const matchedCategory: any = statusCategories.find((record: any) => record.name === category);
  const { color, backgroundHexcode: backgroundColor } = matchedCategory;

  const style = { backgroundColor, color };
  return style;
};

export const getDropdownBackground = (category: any): any => {
  const matchedCategory: any = statusCategories.find((record: any) => record.name === category);
  const background = matchedCategory.background;
  return background;
};
